'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Bell, User, Search, Menu, X, LogOut, LayoutDashboard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const SAMPLE_NOTIFICATIONS = [
  { id: 1, title: 'Booking Confirmed', message: 'Your Ganesh Puja is confirmed for tomorrow at 9:00 AM.', time: '2 min ago', read: false },
  { id: 2, title: 'Priest Assigned', message: 'Pandit Ramesh Kumar has been assigned to your ritual.', time: '1 hr ago', read: false },
  { id: 3, title: 'Reminder', message: 'Satyanarayana Puja is scheduled for this Sunday.', time: '3 hrs ago', read: true },
  { id: 4, title: 'New Ritual Added', message: 'Navgraha Shanti Puja is now available. Book early!', time: '1 day ago', read: true },
];

export default function HomeNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifOpen, setNotifOpen] = useState(false);
  const router = useRouter();
  const { user, signOut, loading } = useAuth();
  const searchRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      router?.push('/login');
      router?.refresh();
    } catch {}
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/rituals?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const unreadCount = SAMPLE_NOTIFICATIONS.filter((n) => !n.read).length;
  const displayName = user?.user_metadata?.full_name?.split(' ')?.[0] || user?.email?.split('@')?.[0] || 'Devotee';

  const navLinkClass = scrolled
    ? 'text-sm font-medium text-maroon hover:text-saffron transition-colors focus-ring' :'text-sm font-medium text-yellow-100 hover:text-white transition-colors drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)] focus-ring';

  const iconClass = scrolled
    ? 'text-maroon/70 hover:text-saffron' :'text-yellow-100 hover:text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-md border-b border-gold/20'
          : 'bg-black/20 backdrop-blur-sm'
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/home-screen" className="flex items-center gap-2 focus-ring rounded-lg" aria-label="Yagnick home">
            <div className="w-9 h-9 rounded-full saffron-gradient flex items-center justify-center shadow-md glow-saffron">
              <span className="text-white text-lg" role="img" aria-hidden="true">🪔</span>
            </div>
            <span
              className={`font-display text-xl font-bold tracking-tight ${
                scrolled ? 'text-maroon' : 'text-yellow-100 drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]'
              }`}
            >
              Yagnick
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link href="/home-screen" className={navLinkClass}>Home</Link>
            <Link href="/rituals" className={navLinkClass}>Rituals</Link>
            <Link href="/priests" className={navLinkClass}>Priests</Link>
            <Link href="/ai-guide" className={navLinkClass}>AI Guide</Link>
            {user && (
              <>
                <Link href="/priest-dashboard" className={navLinkClass}>Priest Portal</Link>
                <Link href="/admin" className={navLinkClass}>Admin</Link>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative hidden md:block" ref={searchRef}>
              <button
                onClick={() => setSearchOpen((prev) => !prev)}
                className={`w-9 h-9 flex items-center justify-center rounded-full hover:bg-saffron/10 transition-colors focus-ring ${iconClass}`}
                aria-label="Search"
                aria-expanded={searchOpen}
              >
                <Search size={18} />
              </button>
              {searchOpen && (
                <div className="absolute right-0 top-11 w-72 bg-white rounded-2xl shadow-2xl border border-gold/20 p-3 z-50 animate-scale-in">
                  <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
                    <input
                      autoFocus
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search rituals, priests…"
                      className="flex-1 text-sm text-maroon placeholder-maroon/40 bg-transparent outline-none border-b border-gold/30 pb-1.5"
                      aria-label="Search query"
                    />
                    <button
                      type="submit"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-saffron text-white hover:bg-saffron/80 transition-colors"
                      aria-label="Submit search"
                    >
                      <Search size={14} />
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Notification Bell */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen((prev) => !prev)}
                className={`w-9 h-9 flex items-center justify-center rounded-full hover:bg-saffron/10 transition-colors relative focus-ring ${iconClass}`}
                aria-label={`Notifications — ${unreadCount} unread`}
                aria-expanded={notifOpen}
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none" aria-hidden="true">
                    {unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-11 w-80 bg-white rounded-2xl shadow-2xl border border-gold/20 z-50 overflow-hidden animate-scale-in">
                  <div className="px-4 py-3 border-b border-gold/10 flex items-center justify-between">
                    <span className="text-sm font-semibold text-maroon">Notifications</span>
                    <span className="text-xs text-saffron font-medium">{unreadCount} new</span>
                  </div>
                  <ul className="divide-y divide-gold/10 max-h-72 overflow-y-auto" role="list">
                    {SAMPLE_NOTIFICATIONS.map((notif) => (
                      <li
                        key={notif.id}
                        className={`px-4 py-3 flex gap-3 items-start hover:bg-saffron/5 transition-colors cursor-pointer ${
                          !notif.read ? 'bg-amber-50/60' : ''
                        }`}
                      >
                        <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${!notif.read ? 'bg-saffron' : 'bg-gray-300'}`} aria-hidden="true" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-maroon">{notif.title}</p>
                          <p className="text-xs text-maroon/60 mt-0.5 leading-snug">{notif.message}</p>
                          <p className="text-[10px] text-maroon/40 mt-1">{notif.time}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="px-4 py-2.5 border-t border-gold/10 text-center">
                    <Link href="/dashboard/notifications" className="text-xs text-saffron font-medium hover:underline" onClick={() => setNotifOpen(false)}>
                      View all notifications
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {!loading && (
              user ? (
                <div className="hidden md:flex items-center gap-1.5">
                  <Link
                    href="/dashboard"
                    className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full hover:bg-saffron/10 transition-colors focus-ring ${scrolled ? 'text-maroon/70 hover:text-saffron' : 'text-yellow-100 hover:text-white'}`}
                    title="Dashboard"
                  >
                    <LayoutDashboard size={15} />
                    <span className="hidden lg:block">Dashboard</span>
                  </Link>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${scrolled ? 'bg-saffron/10 border-saffron/20' : 'bg-white/10 border-white/20'}`}>
                    <div className="w-6 h-6 rounded-full saffron-gradient flex items-center justify-center" aria-hidden="true">
                      <User size={13} className="text-white" />
                    </div>
                    <span className={`text-sm font-medium font-body ${scrolled ? 'text-maroon' : 'text-ivory'}`}>{displayName}</span>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full hover:bg-red-50 hover:text-red-600 transition-colors focus-ring ${scrolled ? 'text-maroon/50' : 'text-yellow-100/70 hover:text-red-400'}`}
                    title="Sign Out"
                    aria-label="Sign out"
                  >
                    <LogOut size={15} />
                    <span className="hidden lg:block">Sign Out</span>
                  </button>
                </div>
              ) : (
                <Link
                  href="/login"
                  className="hidden md:flex items-center gap-2 bg-saffron hover:bg-saffron/90 text-white text-sm font-semibold px-4 py-2 rounded-full btn-glow transition-all focus-ring"
                >
                  <User size={15} />
                  Sign In
                </Link>
              )
            )}

            {/* Mobile menu toggle */}
            <button
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-saffron/10 transition-colors focus-ring ${iconClass}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white/98 backdrop-blur-xl border-t border-gold/20 px-4 py-4 space-y-1 animate-scale-in shadow-xl">
          {/* Mobile Search */}
          <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 bg-saffron/5 border border-gold/20 rounded-xl px-3 py-2.5 mb-3">
            <Search size={16} className="text-maroon/40 flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search rituals, priests…"
              className="flex-1 text-sm text-maroon placeholder-maroon/40 bg-transparent outline-none"
              aria-label="Mobile search"
            />
          </form>

          {[
            { href: '/home-screen', label: 'Home' },
            { href: '/rituals', label: 'Rituals' },
            { href: '/priests', label: 'Priests' },
            { href: '/ai-guide', label: 'AI Guide' },
            ...(user ? [
              { href: '/dashboard', label: 'My Dashboard' },
              { href: '/priest-dashboard', label: 'Priest Portal' },
              { href: '/admin', label: 'Admin Panel' },
            ] : []),
          ].map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setMenuOpen(false)}
              className="flex items-center px-3 py-2.5 rounded-xl text-sm font-medium text-maroon hover:bg-saffron/5 hover:text-saffron transition-colors"
            >
              {item.label}
            </Link>
          ))}

          <div className="pt-2 border-t border-gold/15 mt-2">
            {user ? (
              <button
                onClick={() => { setMenuOpen(false); handleSignOut(); }}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut size={15} />
                Sign Out
              </button>
            ) : (
              <Link
                href="/login"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-2 saffron-gradient text-white text-sm font-semibold px-4 py-3 rounded-xl btn-glow"
              >
                <User size={15} />
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}